import type { PageHelpFlow } from './types.js';

const startSteps = [
  { id: 'plan', label: 'بررسی اشتراک و امکانات' },
  { id: 'team', label: 'دعوت و دسترسی همکاران' },
  { id: 'reference', label: 'تکمیل اطلاعات پایه' },
  { id: 'work', label: 'شروع عملیات روزانه' },
  { id: 'review', label: 'بررسی نتیجه و گزارش‌ها' },
] as const;

const aiSteps = [
  { id: 'settings', label: 'فعال‌سازی و دسترسی' },
  { id: 'credit', label: 'بررسی اعتبار مصرف' },
  { id: 'knowledge', label: 'تکمیل دانش سازمانی' },
  { id: 'question', label: 'گفتگو با دستیار' },
  { id: 'verify', label: 'بررسی پاسخ و رکورد اصلی' },
] as const;

function aiFlow(
  currentStepId: string,
  position: string,
  before: string,
  after: string,
): PageHelpFlow {
  return {
    id: 'ai-question-to-followup',
    title: 'از آماده‌سازی دستیار تا پیگیری پاسخ',
    steps: aiSteps,
    role: currentStepId === 'question' ? 'stage' : 'support',
    currentStepId,
    position,
    before,
    after,
    note: 'دانش سازمانی برای افزودن زمینه است و برای همهٔ سؤال‌ها الزامی نیست. نوع پاسخ و امکان اجرا به قابلیت فعال و دسترسی شما بستگی دارد.',
  };
}

export const GENERAL_PAGE_FLOWS: Readonly<Record<string, readonly PageHelpFlow[]>> = {
  home: [
    {
      id: 'daily-work',
      title: 'شروع و پیگیری روز کاری',
      role: 'stage',
      currentStepId: 'prioritize',
      steps: [
        { id: 'prioritize', label: 'مرور کارهای روز' },
        { id: 'module', label: 'انتخاب بخش مربوط' },
        { id: 'work', label: 'انجام کار در صفحهٔ اصلی' },
        { id: 'review', label: 'بررسی نتیجه' },
      ],
      position: 'خانه نقطهٔ شروع و اولویت‌بندی است؛ برای انجام هر کار وارد بخش اصلی آن می‌شوید.',
      before: 'ورود به فضای کاری و مشخص‌بودن دسترسی‌های شما.',
      after: 'بازکردن کار یا بخش مرتبط و پیگیری نتیجه در همان‌جا.',
    },
  ],
  users: [
    {
      id: 'workspace-start',
      title: 'آماده‌سازی سازمان برای کار تیمی',
      role: 'support',
      currentStepId: 'team',
      steps: startSteps,
      position:
        'این صفحه پیش‌نیاز کار تیمی است: همکار را دعوت می‌کنید و دسترسی مناسب او را تعیین می‌کنید.',
      before: 'فضای کاری و ظرفیت کاربران باید برای دعوت همکار آماده باشند.',
      after: 'همکار دعوت را می‌پذیرد و در محدودهٔ مجوز خودش وارد کارهای سازمان می‌شود.',
    },
  ],
  subscription: [
    {
      id: 'workspace-start',
      title: 'آماده‌سازی سازمان برای کار تیمی',
      role: 'support',
      currentStepId: 'plan',
      steps: startSteps,
      position:
        'اینجا امکانات و ظرفیت قابل‌استفادهٔ سازمان را پیش از برنامه‌ریزی کار تیم بررسی می‌کنید.',
      before: 'فضای کاری ایجاد شده و حساب مجاز مدیریت اشتراک وارد شده است.',
      after: 'با توجه به ظرفیت و بخش‌های فعال، کاربران و کارهای موردنیاز را آماده می‌کنید.',
      note: 'اعتبار مصرف هوش مصنوعی از کیف پول دستیار پیگیری می‌شود.',
    },
  ],
  security: [
    {
      id: 'account-security',
      title: 'بررسی امنیت ورودها',
      role: 'stage',
      currentStepId: 'inspect',
      steps: [
        { id: 'login', label: 'ورود به حساب' },
        { id: 'inspect', label: 'بررسی نشست و احراز هویت' },
        { id: 'followup', label: 'پیگیری مورد ناآشنا' },
      ],
      position: 'این صفحه محل مشاهده و بررسی وضعیت امنیتی است.',
      before: 'یک ورود یا روش احراز هویت برای حساب ثبت شده باشد.',
      after: 'در صورت مشاهدهٔ مورد ناآشنا، آن را با مدیر سازمان یا پشتیبانی پیگیری می‌کنید.',
      note: 'نمایش یک نشست به معنی وجود امکان بستن آن از همین صفحه نیست.',
    },
  ],
  'ui-kit': [
    {
      id: 'recognize-ui',
      title: 'شناخت ظاهر و پیام‌های نرم‌افزار',
      role: 'support',
      currentStepId: 'examples',
      steps: [
        { id: 'need', label: 'شناخت جزء موردنیاز' },
        { id: 'examples', label: 'دیدن نمونه و حالت‌ها' },
        { id: 'interpret', label: 'تشخیص همان حالت در صفحهٔ کاری' },
      ],
      position: 'این صفحه مرجع آموزشی ظاهر دکمه‌ها و پیام‌هاست؛ مرحله‌ای از ثبت عملیات شرکت نیست.',
      before: 'می‌خواهید معنی ظاهر یا حالت یک جزء را بشناسید.',
      after: 'در صفحهٔ کاری خود، همان پیام یا حالت را بهتر تشخیص می‌دهید.',
    },
  ],
  'import-export': [
    {
      id: 'import-data',
      title: 'از فایل قبلی تا دادهٔ ثبت‌شده در ساختار',
      role: 'overview',
      steps: [
        { id: 'template', label: 'تهیهٔ قالب مناسب' },
        { id: 'upload', label: 'انتخاب فایل' },
        { id: 'review', label: 'بررسی و اصلاح داده‌ها' },
        { id: 'preview', label: 'پیش‌نمایش و تأیید' },
        { id: 'commit', label: 'ثبت و کنترل مقصد' },
      ],
      position:
        'این صفحه چند مرحلهٔ ورود اطلاعات را یکجا پوشش می‌دهد؛ وضعیت مرحله را از فضای بررسی همان فایل می‌خوانید.',
      before: 'اطلاعات مبدا و قالب بخش موردنظر را آماده کنید؛ کدهای مشترک را هماهنگ نگه دارید.',
      after:
        'بعد از ثبت، نتیجه و رکوردهای مقصد را در بخش‌های مشتریان، کالا، فروش، مالی یا انبار بررسی کنید.',
      note: 'بارگذاری، تحلیل و پیش‌نمایش با ثبت نهایی داده متفاوت‌اند.',
    },
  ],
  'ai-chat': [
    aiFlow(
      'question',
      'اینجا سؤال را مطرح می‌کنید و پاسخ دستیار را می‌بینید؛ سپس برای تصمیم یا اقدام، منبع و رکورد اصلی را کنترل می‌کنید.',
      'دستیار و دسترسی لازم فعال باشند و مصرف مجاز در دسترس باشد.',
      'پاسخ را با منبع اصلی تطبیق دهید و پیگیری لازم را در بخش عملیاتی مربوط انجام دهید.',
    ),
  ],
  'ai-documents': [
    aiFlow(
      'knowledge',
      'این صفحه زمینهٔ شناخت شرکت و منابع متنی مورد استفادهٔ دستیار را آماده می‌کند.',
      'متن یا فایل معتبر سازمانی و مجوز مدیریت آن را آماده کنید.',
      'پس از آماده و فعال‌شدن منبع، می‌توانید دربارهٔ موضوع مرتبط در گفتگو سؤال بپرسید.',
    ),
  ],
  'ai-wallet': [
    aiFlow(
      'credit',
      'این صفحه وضعیت اعتبار مصرف و خریدهای دستیار را روشن می‌کند؛ خودش تحلیل یا کار عملیاتی اجرا نمی‌کند.',
      'موجودی، سفارش‌های قبلی و نتیجهٔ پرداخت احتمالی را بررسی کنید.',
      'پس از روشن‌شدن وضعیت اعتبار، به گفتگو یا درخواست قابل‌اجرا برگردید.',
    ),
  ],
  'ai-settings': [
    aiFlow(
      'settings',
      'مدیر مجاز، وضعیت فعال‌سازی و حدود استفاده از دستیار را در این بخش آماده می‌کند.',
      'مجوز مدیریت دستیار و قواعد موردنظر سازمان مشخص باشند.',
      'نتیجهٔ ذخیره را کنترل کنید؛ سپس مصرف، منابع و استفادهٔ تیم را پیگیری کنید.',
    ),
  ],
  account: [
    {
      id: 'personal-profile',
      title: 'آماده‌سازی حساب شخصی برای کار',
      role: 'stage',
      currentStepId: 'profile',
      steps: [
        { id: 'login', label: 'ورود به حساب' },
        { id: 'profile', label: 'بررسی مشخصات و ظاهر' },
        { id: 'save', label: 'ذخیرهٔ تغییرات' },
        { id: 'work', label: 'ادامهٔ کار در سازمان' },
      ],
      position: 'این صفحه محل تکمیل یا اصلاح تنظیمات شخصی شماست.',
      before: 'با حساب خودتان وارد فضای کاری شده باشید.',
      after: 'پیام ذخیره را بررسی کنید و با مشخصات و ظاهر تنظیم‌شده کار را ادامه دهید.',
      note: 'تغییر پروفایل، مجوزهای سازمانی را تغییر نمی‌دهد.',
    },
  ],
  notifications: [
    {
      id: 'notification-followup',
      title: 'از رویداد تا رسیدگی',
      role: 'stage',
      currentStepId: 'read',
      steps: [
        { id: 'event', label: 'ثبت رویداد در سیستم' },
        { id: 'notice', label: 'ایجاد اعلان مرتبط' },
        { id: 'read', label: 'مشاهدهٔ اعلان' },
        { id: 'record', label: 'بازکردن رکورد مرتبط' },
        { id: 'act', label: 'رسیدگی و بررسی نتیجه' },
      ],
      position: 'این صفحه مرحلهٔ باخبرشدن و پیدا کردن محل پیگیری است.',
      before: 'اعلانی برای حساب شما ثبت شده باشد.',
      after: 'در صورت وجود لینک مرتبط، رکورد را باز کنید و کار را در همان صفحه پیگیری کنید.',
      note: 'خوانده‌شدن اعلان به معنی انجام‌شدن کار مرتبط نیست.',
    },
  ],
  'quick-add': [
    {
      id: 'quick-start',
      title: 'از انتخاب کار تا ثبت در فرم اصلی',
      role: 'stage',
      currentStepId: 'choose',
      steps: [
        { id: 'choose', label: 'انتخاب نوع کار' },
        { id: 'form', label: 'بازشدن فرم اصلی' },
        { id: 'complete', label: 'تکمیل اطلاعات' },
        { id: 'result', label: 'ثبت و بررسی نتیجه' },
      ],
      position: 'افزودن سریع فقط نقطهٔ انتخاب و ورود به فرم اصلی است.',
      before: 'بدانید چه نوع رکورد یا کاری می‌خواهید ایجاد کنید و دسترسی آن را داشته باشید.',
      after: 'فرم مقصد را تکمیل کنید؛ نتیجهٔ واقعی ثبت را از همان فرم یا رکورد بخوانید.',
    },
  ],
  'my-tasks': [
    {
      id: 'personal-task-work',
      title: 'از تعریف کار تا رسیدگی روزانه',
      role: 'stage',
      currentStepId: 'choose',
      steps: [
        { id: 'create', label: 'تعریف کار' },
        { id: 'assign', label: 'مشخص‌کردن مسئول و موعد' },
        { id: 'choose', label: 'مرور کارهای من' },
        { id: 'work', label: 'انجام کار در صفحهٔ مربوط' },
        { id: 'review', label: 'پیگیری وضعیت و نتیجه' },
      ],
      position: 'اینجا از میان کارهای نمایش‌داده‌شده، مورد بعدی برای رسیدگی را پیدا می‌کنید.',
      before: 'کاری برای نمایش به شما وجود داشته باشد.',
      after: 'جزئیات کار را باز کنید و نتیجهٔ تغییر وضعیت را در همان رکورد کنترل کنید.',
    },
  ],
  'mobile-menu': [
    {
      id: 'find-workspace-page',
      title: 'پیدا کردن صفحهٔ کاری',
      role: 'stage',
      currentStepId: 'section',
      steps: [
        { id: 'need', label: 'مشخص‌کردن کار موردنظر' },
        { id: 'section', label: 'انتخاب بخش از منو' },
        { id: 'page', label: 'بازکردن صفحهٔ مربوط' },
        { id: 'work', label: 'انجام کار و بررسی نتیجه' },
      ],
      position: 'منو مرحلهٔ پیدا کردن مقصد است؛ خودِ عملیات در صفحهٔ مقصد انجام می‌شود.',
      before: 'نوع کاری را که می‌خواهید انجام دهید مشخص کنید.',
      after: 'بخش در دسترس را باز کنید و راهنمای همان صفحه را برای ادامه بخوانید.',
    },
  ],
};
